import { FormGroup, Label, Switch, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { useFormikContext } from "formik";
import React from "react";
import { DisplayStepOption, Field, FieldType } from "types";
import { BeforePaymentField } from "./QuestionFormModal";
import { isBeforePaymentType, isMarketingConsentType } from "@/models/Form/Questionnaire";
import { QuestionnaireModalType } from "@/models/Form/QuestionnaireModalType";
import { hasX2CheckoutWithFullProbability } from "@/models/Seller";

interface QuestionFormSettingsProps {
    isMembershipProduct?: boolean;
    isAttendeeUnique?: boolean;
    hasQuestionnairePlugin: boolean;
    modalType: QuestionnaireModalType;
    hasWaiversEnabled?: boolean;
}

export const QuestionFormSettings = ({
    isMembershipProduct,
    isAttendeeUnique,
    hasQuestionnairePlugin,
    modalType,
    hasWaiversEnabled,
}: QuestionFormSettingsProps) => {
    const { setFieldValue, values } = useFormikContext<Field>();

    const isStepWaiverSigning = values.displayStep === DisplayStepOption.WAIVER_SIGNING;
    const isConsentField = isMarketingConsentType(modalType);
    const isBeforePayment = isBeforePaymentType(modalType);
    const isAddon = modalType === QuestionnaireModalType.ADDON;
    const isPickupSelect = modalType === QuestionnaireModalType.PICKUP && values.type === FieldType.PICKUP;

    // When isBeforePaymentType is true or X2 checkout is enabled and waiver app is not installed, we will be showing the switch button for Before payment
    const showBeforePaymentField = (isBeforePayment || hasX2CheckoutWithFullProbability()) && !hasQuestionnairePlugin;
    // Show waiver signing switch when waiver app is installed and X2 checkout is not at full probability
    // For Before Payment fields with waiver app installed we show step buttons in v1
    const showWaiverSigningSwitch =
        !isMembershipProduct &&
        hasQuestionnairePlugin &&
        !hasX2CheckoutWithFullProbability() &&
        !isBeforePayment &&
        !isAddon;

    const shouldDisableWaiverSwitch = hasWaiversEnabled ? !isAttendeeUnique : true;

    return (
        <FormGroup className="flex flex-col items-start space-y-1">
            <Label className="mb-0 flex-grow">Settings</Label>

            <ConsentSwitch
                isAddon={isAddon}
                isConsentField={isConsentField}
                values={values}
                setFieldValue={setFieldValue}
            />

            {(showBeforePaymentField || isAddon) && (
                <BeforePaymentField modalType={modalType} isMembershipProduct={isMembershipProduct} />
            )}

            <OrganizerOnlySwitch
                isStepWaiverSigning={isStepWaiverSigning}
                isAttendeeUnique={isAttendeeUnique}
                isPickup={isPickupSelect}
                isMembershipProduct={isMembershipProduct}
                values={values}
                setFieldValue={setFieldValue}
            />

            {showWaiverSigningSwitch && (
                <WaiverSigningSwitch
                    values={values}
                    modalType={modalType}
                    isStepWaiverSigning={isStepWaiverSigning}
                    isDisabled={shouldDisableWaiverSwitch}
                    setFieldValue={setFieldValue}
                />
            )}
        </FormGroup>
    );
};

interface ConsentSwitchProps {
    isConsentField: boolean;
    isAddon: boolean;
    values: Field;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void;
}

const ConsentSwitch = ({ isConsentField, values, isAddon, setFieldValue }: ConsentSwitchProps) => {
    if (!isConsentField) {
        return (
            <div data-testid="questionnaire-form-modal-required-toggle">
                <Switch.Group>
                    <Switch
                        disabled={isAddon}
                        isChecked={values.required}
                        onChange={(value: boolean) => setFieldValue("required", value)}
                    />
                    <Switch.Label className="ml-3">Required</Switch.Label>
                </Switch.Group>
            </div>
        );
    }

    return (
        <Switch.Group>
            <Switch
                isChecked={values.optInByDefault}
                onChange={(value: boolean) => setFieldValue("optInByDefault", value)}
            />
            <Switch.Label className="ml-3">Opt-in by default</Switch.Label>
        </Switch.Group>
    );
};

interface OrganizerOnlySwitchProps {
    isStepWaiverSigning: boolean;
    isAttendeeUnique?: boolean;
    isMembershipProduct?: boolean;
    isPickup?: boolean;
    values: Field;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void;
}

const OrganizerOnlySwitch = ({
    isStepWaiverSigning,
    isAttendeeUnique,
    isMembershipProduct,
    isPickup,
    values,
    setFieldValue,
}: OrganizerOnlySwitchProps) => {
    return (
        <Tooltip
            disabled={!isStepWaiverSigning && isAttendeeUnique && !isPickup}
            content={
                !isAttendeeUnique
                    ? "This option is disabled when attendees are not unique"
                    : isStepWaiverSigning
                    ? "Questions presented in a waiver cannot be limited to only organizer"
                    : "Pickup questions are always asked to the organizer only"
            }
        >
            <div data-testid="questionnaire-form-modal-only-organizer-toggle">
                <Switch.Group>
                    <Switch
                        isChecked={values.organizerOnly}
                        disabled={isStepWaiverSigning || isAttendeeUnique === false || isPickup}
                        onChange={(value: boolean) => {
                            setFieldValue("organizerOnly", value);
                            if (value && isStepWaiverSigning) {
                                setFieldValue("displayStep", null);
                            }
                        }}
                    />
                    <Switch.Label
                        className={clsx(
                            "ml-3",
                            (isAttendeeUnique === false || isStepWaiverSigning || isPickup) && "text-gray-dark",
                        )}
                    >
                        {isMembershipProduct ? "Ask purchaser only" : "Ask only organizer"}
                    </Switch.Label>
                </Switch.Group>
            </div>
        </Tooltip>
    );
};

interface WaiverSigningSwitchProps {
    values: Field;
    isStepWaiverSigning: boolean;
    modalType: QuestionnaireModalType;
    isDisabled?: boolean;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void;
}

const WaiverSigningSwitch = ({
    values,
    isStepWaiverSigning,
    modalType,
    isDisabled,
    setFieldValue,
}: WaiverSigningSwitchProps) => (
    <Tooltip
        disabled={!values.organizerOnly}
        content={
            values.organizerOnly
                ? "Questions for the organizer cannot be presented to all guests who sign the waiver"
                : ""
        }
    >
        <span className="w-30">
            <Switch.Group>
                <Switch
                    isChecked={isStepWaiverSigning}
                    disabled={values.organizerOnly || isDisabled}
                    onChange={(value: boolean) => {
                        setFieldValue(
                            "displayStep",
                            value
                                ? DisplayStepOption.WAIVER_SIGNING
                                : isBeforePaymentType(modalType)
                                ? DisplayStepOption.BEFORE_PAYMENT
                                : DisplayStepOption.AFTER_PAYMENT,
                        );
                        if (value && values.organizerOnly) {
                            setFieldValue("organizerOnly", false);
                        }
                    }}
                />
                <Switch.Label className={clsx("ml-3", values.organizerOnly && "text-gray-dark")}>
                    Ask during waiver signing
                </Switch.Label>
            </Switch.Group>
        </span>
    </Tooltip>
);
